import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { HTTP_STATUSES, PAGINATION } from "@miplatform/constants";
import { bugsnag, muiFilterToODataFilter } from "@miplatform/utils";
import { useDebounce } from "../useDebounce/useDebounce";

const fifteenMinutesAgo = moment().subtract(15, "minutes").toDate();

export const usePagination = (config) => {
  const [pageCount, setPageCount] = useState(0);
  const [counts, setCounts] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [filters, setFilters] = useState({ items: [] });
  const [sort, setSort] = useState(null);
  const debouncedFilters = useDebounce(filters, 300);
  const initialRender = useRef(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: config.defaultPageSize || 50,
  });

  const getParams = () => {
    switch (config.mode) {
      case PAGINATION.SERVER:
        return {
          includeTotal: true,
          pageOffset: paginationModel.page,
          pageSize: paginationModel.pageSize,
        };
      case PAGINATION.CLIENT:
        return { pageOffset: 0, pageSize: 9999 };
      default:
        return { pageOffset: 0, pageSize: 9999 };
    }
  };

  const setPagination = (response, shouldSetExtraData) => {
    if (config.mode === PAGINATION.SERVER) {
      setPageCount(response.headers.totalpages);
      setRowCount(Number(response.headers.totalcount));
      if (shouldSetExtraData && response.headers.counts) {
        setCounts(JSON.parse(response.headers.counts));
      }
    }
  };

  const getPageData = async (shouldSetExtraData = true) => {
    if (!config.getPageData) {
      return;
    }

    const params = getParams();

    try {
      const response = await config.getPageData({
        params,
        filters: filters && muiFilterToODataFilter(filters),
        search: config.search,
        sort: sort?.map((item) => `${item.field} ${item.sort}`),
        top: paginationModel.pageSize,
        skip: paginationModel.page * paginationModel.pageSize,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setPagination(response, shouldSetExtraData);
        if (config.setData) {
          config.setData(response.data);
        }
      }

      if (!config.getPageRecentData) {
        return;
      }

      const recentResponse = await config.getPageRecentData({
        sort: "modifiedOn desc, createdOn desc",
        filters: {
          or: [
            { createdOn: { ge: fifteenMinutesAgo } },
            { modifiedOn: { ge: fifteenMinutesAgo } },
          ],
        },
        top: 5,
      });
      if (recentResponse.status === HTTP_STATUSES.OK) {
        if (config.setRecentData) {
          config.setRecentData(recentResponse.data);
        }
      }
    } catch (error) {
      bugsnag.setContext(
        "usePagination::getPageData: Error fetching page data"
      );
      bugsnag.notify(error);
    }
  };

  useEffect(() => {
    if (initialRender.current && config.mode === PAGINATION.CLIENT) {
      // Fetch data only on initial render for client-side pagination
      getPageData();
      initialRender.current = false;
    } else if (config.mode === PAGINATION.SERVER) {
      // Fetch data every time pagination params change
      getPageData();
    }
  }, [paginationModel, sort, config.search, debouncedFilters]);

  useEffect(() => {
    onResetPage();
  }, [config.search, config.refreshPagination]);

  const handlePaginationModel = (pageModel) => {
    setPaginationModel(pageModel);
  };

  const onSortChange = (sortModel) => {
    setSort(sortModel);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const onResetPage = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const onFilterChange = (filter) => {
    setFilters(filter);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  return {
    rowCount,
    pageCount,
    counts,
    filters,
    sort,
    paginationModel,
    getPageData,
    handlePaginationModel,
    onSortChange,
    onFilterChange,
    onResetPage,
  };
};
